// src/components/auth/LoginForm.js - Premium Central Design with Platform Colors
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import EbiosensLogo from '../Brand/EbiosensLogo';
import MfaVerificationPrompt from './MfaVerificationPrompt';
import MfaSetupModal from './MfaSetupModal';

const LoginForm = () => {
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [inputErrors, setInputErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);


  const { login, isLoading, error: authError, isAuthenticated, user, loginAttemptsRemaining, rateLimitResetTime, mfaPending, confirmMfaLogin, cancelMfaLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && user && !mfaPending) {
      const from = location.state?.from?.pathname || '/app';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, mfaPending, navigate, location]);

  useEffect(() => {
    if (authError) {
      setFormError(authError);
    } else {
      setFormError('');
    }
  }, [authError]);

  useEffect(() => {
    if (mfaPending?.setupRequired) {
      setShowMfaSetup(true);
    }
  }, [mfaPending]);

  const validateForm = () => {
    const errors = {};
    let hasErrors = false;

    if (!email.trim()) {
      errors.email = t('login.error_email_required');
      hasErrors = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('login.error_email_invalid');
      hasErrors = true;
    }

    if (!password) {
      errors.password = t('login.error_password_required');
      hasErrors = true;
    }

    setInputErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setInputErrors({});

    if (!validateForm()) return;

    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setPassword('');
        if (result?.mfaSetupRequired) {
          setShowMfaSetup(true);
        }
      }
    } catch (err) {
      console.error('Login attempt failed:', err);
    }
  };

  const handleMfaVerify = async (totpCode, trustDevice) => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const result = await confirmMfaLogin(totpCode, trustDevice);
      if (!result.success) {
        setMfaError(result.error || 'Code invalide');
      }
    } catch (err) {
      setMfaError(err.message || 'Erreur de vérification');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaCancel = () => {
    cancelMfaLogin();
    setMfaError('');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100">

      {/* Background Ambience - Platform Blue Accent */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] bg-blue-600/15 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-[-15%] right-[-10%] w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '12s' }} />
        <div className="absolute top-[40%] right-[20%] w-[30vw] h-[30vw] bg-sky-400/5 rounded-full blur-[60px]" />
      </div>

      {/* Subtle Grid Pattern */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      ></div>

      {/* Language Toggle (Floating) */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/60 hover:bg-gray-700/70 border border-gray-700/50 backdrop-blur-md text-gray-300 hover:text-white transition-all text-sm font-medium shadow-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
          </svg>
          <span>{i18n.language === 'fr' ? 'EN' : 'FR'}</span>
        </button>
      </div>

      {/* CENTRAL CARD */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring', damping: 20 }}
        className="w-full max-w-md mx-4 relative z-10"
      >
        {/* Logos Row - Clean & Large */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-center gap-4 sm:gap-6 md:gap-10 mb-1.5"
        >
          {/* ACG Logo */}
          <motion.img
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            src="/acg-logo.png"
            alt="ACG Cybersecurity"
            className="h-16 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-300 cursor-pointer rounded-xl flex-shrink-0"
          />

          {/* EBIOS RM Logo */}
          <motion.img
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            src="/ebios-rm-logo.png"
            alt="EBIOS RM"
            className="h-16 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-300 cursor-pointer rounded-xl flex-shrink-0"
          />

          {/* ANSSI Logo */}
          <motion.img
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            src="/anssi-logo.png"
            alt="ANSSI"
            className="h-16 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-300 cursor-pointer rounded-xl flex-shrink-0"
          />
        </motion.div>

        {/* Login Form Card */}
        <div className="bg-gray-800/70 backdrop-blur-xl border border-gray-700/50 rounded-2xl px-8 md:px-10 pt-4 pb-8 md:pb-10 shadow-2xl shadow-black/30 relative overflow-hidden">

          {/* Decorative top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600"></div>

          {/* Header Section: Branding */}
          <div className="text-center mb-3">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <div className="flex justify-center mb-1">
                <EbiosensLogo size={68} withWordmark={false} variant="dark" />
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-1">
                <span className="text-white">EBIOS</span><span className="text-blue-500">ENS</span>
              </h1>
              <p className="text-base text-white-400 font-medium tracking-wide">

              </p>
              <p className="text-base text-white-400 font-medium tracking-wide">
                ACG CYBERSECURITY
              </p>
            </motion.div>
          </div>

          <div className="relative">
            {/* Form Title */}
            <div className="mb-6 text-center overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={i18n.language + '-title'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {t('login.title')}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {i18n.language === 'fr' ? 'Bienvenue, veuillez vous identifier.' : 'Welcome back, please login.'}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Error Zone */}
            <AnimatePresence>
              {formError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2"
                >
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>{formError}</span>
                </motion.div>
              )}
              {loginAttemptsRemaining !== null && loginAttemptsRemaining <= 3 && loginAttemptsRemaining > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm flex items-start gap-2"
                >
                  <span className="text-base">⚠</span>
                  <p>{t('login.attempts_warning', { count: loginAttemptsRemaining })}</p>
                </motion.div>
              )}
              {loginAttemptsRemaining === 0 && rateLimitResetTime && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2"
                >
                  <span className="text-base">✕</span>
                  <p>{t('login.blocked_message', { time: rateLimitResetTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {mfaPending && !mfaPending.setupRequired ? (
                <motion.div
                  key="mfa"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  className="py-4"
                >
                  <MfaVerificationPrompt
                    onVerify={handleMfaVerify}
                    onCancel={handleMfaCancel}
                    isLoading={mfaLoading}
                    error={mfaError}
                  />
                </motion.div>
              ) : (
                <motion.div key="login">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {t('login.email_label')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <svg className={`w-5 h-5 transition-colors ${focusedField === 'email' ? 'text-blue-500' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    disabled={isLoading}
                    placeholder={t('login.email_placeholder')}
                    className={`w-full py-3 pl-11 pr-4 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-200 ${inputErrors.email
                      ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : focusedField === 'email'
                        ? 'border-blue-500 bg-gray-900/70 ring-2 ring-blue-500/20'
                        : 'border-gray-600 hover:border-gray-500'
                      }`}
                  />
                </div>
                {inputErrors.email && (
                  <p className="mt-1.5 text-xs text-red-400 font-medium">
                    {inputErrors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {t('login.password_label')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <svg className={`w-5 h-5 transition-colors ${focusedField === 'password' ? 'text-blue-500' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    disabled={isLoading}
                    placeholder={t('login.password_placeholder')}
                    className={`w-full py-3 pl-11 pr-11 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-200 ${inputErrors.password
                      ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : focusedField === 'password'
                        ? 'border-blue-500 bg-gray-900/70 ring-2 ring-blue-500/20'
                        : 'border-gray-600 hover:border-gray-500'
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                {inputErrors.password && (
                  <p className="mt-1.5 text-xs text-red-400 font-medium">
                    {inputErrors.password}
                  </p>
                )}
              </div>

              <div className="pt-2">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3.5 rounded-lg font-semibold text-white shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${isLoading
                    ? 'bg-blue-600/50 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/25 hover:shadow-blue-500/30'
                    }`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      <span>{t('login.submitting')}</span>
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('login.submit')}
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    </span>
                  )}
                </motion.button>
              </div>
            </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} EBIOSENS • {t('login.system_desc')}
        </div>
      </motion.div>

      {/* MFA Setup Modal */}
      <MfaSetupModal
        isOpen={showMfaSetup}
        onClose={() => setShowMfaSetup(false)}
        onComplete={() => {
          setShowMfaSetup(false);
          cancelMfaLogin();
          setEmail('');
          setPassword('');
          navigate('/app');
        }}
      />
    </div>
  );
};

export default LoginForm;
